import { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

const ProtectedRoute = ({ children, requiredPermission = null, requiredRole = null, requiredUserId = null }) => {
  const { isAuthenticated, hasPermission, hasRole, loading, user } = useAuth();
  const [permissionLoading, setPermissionLoading] = useState(false);
  const hasAttemptedRefresh = useRef(false);

  // Support requiredPermission as string or array (OR logic for arrays)
  const checkPermission = () => {
    if (!requiredPermission) return true;
    if (Array.isArray(requiredPermission)) {
      return requiredPermission.some(p => hasPermission(p));
    }
    return hasPermission(requiredPermission);
  };

  // Refresh user data if we detect missing permissions (only once)
  useEffect(() => {
    if (isAuthenticated && requiredPermission && !checkPermission() && !hasAttemptedRefresh.current) {
      console.log('Permission check failed, user may need to re-login');
      hasAttemptedRefresh.current = true;
      setPermissionLoading(false);
    }
  }, [isAuthenticated, requiredPermission, user]);

  if (loading || permissionLoading) {
    console.log('[ProtectedRoute] Loading authentication...', { loading, permissionLoading });
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading authentication...</div>;
  }

  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('[ProtectedRoute] User authenticated:', {
    requiredPermission,
    requiredRole,
    userPermissions: user?.permissions,
    userRole: user?.role
  });

  if (requiredPermission && !checkPermission()) {
    const permDisplay = Array.isArray(requiredPermission) ? requiredPermission.join(' or ') : requiredPermission;
    console.log('Missing permission:', permDisplay, 'User permissions:', user?.permissions);
    // Instead of redirect loop, show error page
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Access Denied</h1>
        <p>You don't have permission to access this application.</p>
        <p>Permission required: {permDisplay}</p>
        <a href="/hub">Back to HUB</a>
      </div>
    );
  }

  if (requiredRole && !hasRole(requiredRole)) {
    console.log('Missing role:', requiredRole, 'User role:', user?.role);
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Access Denied</h1>
        <p>You don't have the required role to access this resource.</p>
        <p>Required role: {requiredRole}</p>
        <a href="/hub">Back to HUB</a>
      </div>
    );
  }

  if (requiredUserId && user?.id !== requiredUserId) {
    console.log('User ID mismatch:', requiredUserId, 'User ID:', user?.id);
    return <Navigate to="/hub" replace />;
  }

  return children;
};

export default ProtectedRoute;
